import { useEffect, useState, type CSSProperties } from 'react';
import { useExplorerEnv } from './explorer_env.js';

/**
 * Fila do esqueleto da lista. Generosa DE PROPÓSITO: quem a interrompe é o
 * `overflow: hidden` do `.fex-skel` (explorer.css), e é essa dupla que faz o
 * esqueleto acabar na borda de baixo da coluna em qualquer altura de janela. A
 * fila fixa de 5 media 500px numa coluna de 811 e deixava o resto em branco —
 * um vão que lê como "acabou", não como "está vindo".
 */
const SKEL_ROWS = 8;

/** A partir de quando a espera precisa de PALAVRA (#2926). 15s é a marca em que
 *  a busca já saiu do comum (p50 de 27s, mas o quadro de 12s é rotina) sem ainda
 *  ser sintoma: a cauda real vai a 80s, e um esqueleto mudo por esse tempo lê
 *  como tela travada — foi assim que uma busca de 40,9s CONCLUÍDA virou a
 *  pergunta "não foi possível buscar os voos?". */
const LONG_SEARCH_MS = 15_000;

/** A espera passou do comum? `searchKey` novo zera a contagem — o timer é da
 *  BUSCA, não da montagem do componente. */
function useLongSearch(searchKey: string): boolean {
  // The search the clock ran out on: a new key is not long until its own clock does.
  const [longFor, setLongFor] = useState<string | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      setLongFor(searchKey);
    }, LONG_SEARCH_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [searchKey]);
  return longFor === searchKey;
}

/**
 * A busca em voo. Esqueleto de LINHAS, não um "Buscando…" solto: a busca do
 * provider leva de 12s (doméstico) a 40s+ (internacional), e nesse tempo uma
 * frase no vazio não diz que vem uma lista nem quanto dela vem. O `output` já
 * tem role=status, então o leitor de tela ouve o texto da busca — e ouve
 * também o aviso da espera longa, que entra no MESMO live region.
 *
 * O aviso NÃO é desfecho — ele diz que a busca CONTINUA. Quem termina a espera
 * é a resposta (`search_ok`) ou a falha (`search_failed`), nunca o relógio.
 */
export function ExplorerSkeleton({ searchKey }: Readonly<{ searchKey: string }>) {
  const { copy } = useExplorerEnv();
  const long = useLongSearch(searchKey);
  return (
    <output className="fex-skel gap-6" aria-label={copy.page.searching}>
      {long ? <p className="fex-skel-note">{copy.page.searchingLong}</p> : null}
      {Array.from({ length: SKEL_ROWS }, (_, n) => (
        <span
          key={n}
          className="fex-skel-row"
          // A escada de atraso vive numa custom property (que HERDA) em vez das
          // regras `nth-child` de antes: a fila deixou de ter tamanho fixo, e
          // cinco regras não alcançavam a sexta linha.
          style={{ '--fex-skel-delay': `${(n * 0.09).toFixed(2)}s` } as CSSProperties}
        >
          {/* A forma da `.fq-leg`: em cima a data à esquerda com a companhia na
              outra ponta, embaixo a linha do tempo com as duas pontas de horário.
              É o que faz o esqueleto OCUPAR a linha em vez de deixar duas barras
              soltas num card largo. */}
          <span className="fex-skel-main">
            <span className="fex-skel-head">
              <span className="fex-skel-bar is-date" />
              <span className="fex-skel-bar is-logo" />
              <span className="fex-skel-bar is-no" />
            </span>
            <span className="fex-skel-trip">
              <span className="fex-skel-bar is-pt" />
              <span className="fex-skel-bar is-line" />
              <span className="fex-skel-bar is-pt" />
            </span>
          </span>
          {/* O trilho da direita é o que a linha real tem de mais próprio: preço e
              "Ver tarifas" atrás de uma divisória. Sem ele o esqueleto prometia um
              bloco chapado e a lista pousava com uma anatomia que ninguém viu vir. */}
          <span className="fex-skel-rail">
            <span className="fex-skel-bar is-price" />
            <span className="fex-skel-bar is-cta" />
          </span>
        </span>
      ))}
    </output>
  );
}
